<template>
  <div>
    <b-card>
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <b-card-title class="mb-0">{{ $t('message.worker_permissions_title') }}</b-card-title>
        <div class="d-flex align-items-center flex-wrap">
          <div v-if="!isCreateMode" class="d-flex align-items-center small text-muted mr-2">
            <feather-icon icon="CheckSquareIcon" size="14" class="text-primary mr-25" />
            {{ $t('message.permission_legend_default') }}
            <feather-icon icon="CheckSquareIcon" size="14" class="text-success mr-25 ml-2" />
            {{ $t('message.permission_legend_custom') }}
          </div>
          <b-button
            v-if="!isCreateMode"
            v-b-modal="'modal-reset-worker-permissions'"
            variant="outline-danger"
            size="sm"
            :disabled="isLoading || !hasOverrides"
            class="mr-1"
          >
            {{ $t('message.permission_reset_default') }}
          </b-button>
          <b-button variant="outline-grey" size="sm" :disabled="isLoading" @click="toggleAll">
            <feather-icon :icon="allExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" class="mr-25" />
            {{ allExpanded ? $t('message.collapse_all') : $t('message.expand_all') }}
          </b-button>
        </div>
      </div>

      <b-alert
        variant="primary"
        show
        class="mb-0 mt-1"
      >
        <div class="p-1 d-flex align-items-start">
          <feather-icon
            icon="InfoIcon"
            size="18"
            class="mr-50 flex-shrink-0"
          />
          <span class="small">{{ isCreateMode ? $t('message.worker_permissions_create_info') : $t('message.worker_permissions_page_info') }}</span>
        </div>
      </b-alert>

      <b-modal
        id="modal-reset-worker-permissions"
        centered
        no-close-on-backdrop
        modal-class="modal-danger"
        ok-variant="danger"
        cancel-variant="outline-secondary"
        :title="$t('message.confirm_action')"
        :ok-title="$t('message.permission_reset_default')"
        :cancel-title="$t('message.cancel')"
        @ok="resetOverride"
      >
        {{ $t('message.confirm_reset_worker_permissions') }}
      </b-modal>
    </b-card>

    <div v-if="isLoading" class="text-center py-3">
      <b-spinner variant="primary" />
    </div>

    <template v-else>
      <b-card v-for="group in groups" :key="group.headerKey" no-body>
        <div class="d-flex align-items-center px-2 py-1" style="cursor: pointer;" @click="expanded[group.headerKey] = !expanded[group.headerKey]">
          <feather-icon :icon="expanded[group.headerKey] ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" class="mr-50" />
          <h5 class="mb-0">{{ $t(group.headerKey) }}</h5>
          <span class="text-muted small ml-50">({{ group.rows.length }})</span>
        </div>

        <b-collapse v-model="expanded[group.headerKey]">
          <b-table-simple hover responsive small class="mb-0 worker-permission-matrix">
            <b-tbody>
              <b-tr v-for="row in group.rows" :key="row.moduleKey">
                <b-td>
                  <div class="module-name d-flex align-items-center">
                    <span v-if="row.moduleNavKey.parentKey">{{ $t(row.moduleNavKey.parentKey) }} — </span>
                    <span>{{ $t(row.moduleNavKey.key) }}</span>
                    <feather-icon
                      v-if="moduleNotes(row.moduleKey).length"
                      :id="`worker-module-note-icon-${row.moduleKey}`"
                      icon="InfoIcon"
                      size="14"
                      class="text-warning ml-50 flex-shrink-0 module-note-icon"
                    />
                    <b-popover
                      v-if="moduleNotes(row.moduleKey).length"
                      :target="`worker-module-note-icon-${row.moduleKey}`"
                      triggers="click"
                      placement="top"
                      custom-class="module-note-popover"
                    >
                      <p
                        v-for="(note, i) in moduleNotes(row.moduleKey)"
                        :key="i"
                        class="mb-0 small"
                      >
                        {{ note }}
                      </p>
                    </b-popover>
                  </div>
                  <div class="small text-primary">{{ row.moduleKey }}</div>
                </b-td>
                <b-td>
                  <div class="permission-grid">
                    <b-form-checkbox
                      :checked="viewMode(row.cell) === 'all'"
                      :class="row.overridden.view ? 'custom-control-success' : 'custom-control-primary'"
                      :disabled="isCreateMode"
                      @change="setViewMode(row.moduleKey, viewMode(row.cell) === 'all' ? null : 'all')"
                      class="mb-0"
                    >
                      <span class="small" :class="{ 'text-success permission-override-text': row.overridden.view }">{{ $t('message.permission_view_all') }}</span>
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="viewMode(row.cell) === 'own'"
                      :class="row.overridden.view ? 'custom-control-success' : 'custom-control-primary'"
                      :disabled="isCreateMode"
                      @change="setViewMode(row.moduleKey, viewMode(row.cell) === 'own' ? null : 'own')"
                      class="mb-0"
                    >
                      <span class="small" :class="{ 'text-success permission-override-text': row.overridden.view }">{{ $t('message.permission_view_own') }}</span>
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="!!(row.cell.edit && row.cell.edit.allowed)"
                      :class="row.overridden.edit ? 'custom-control-success' : 'custom-control-primary'"
                      :disabled="isCreateMode"
                      @change="toggleAction(row.moduleKey, 'edit')"
                      class="mb-0"
                    >
                      <span class="small" :class="{ 'text-success permission-override-text': row.overridden.edit }">{{ $t('message.permission_action_edit') }}</span>
                      <span v-if="row.cell.edit && row.cell.edit.allowed && viewMode(row.cell) === 'own'" class="text-muted" style="font-size: 10px;">
                        ({{ $t('message.permission_scope_own') }})
                      </span>
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="!!(row.cell.create && row.cell.create.allowed)"
                      :class="row.overridden.create ? 'custom-control-success' : 'custom-control-primary'"
                      :disabled="isCreateMode"
                      @change="toggleAction(row.moduleKey, 'create')"
                      class="mb-0"
                    >
                      <span class="small" :class="{ 'text-success permission-override-text': row.overridden.create }">{{ $t('message.permission_action_create') }}</span>
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="!!(row.cell.export && row.cell.export.allowed)"
                      :class="row.overridden.export ? 'custom-control-success' : 'custom-control-primary'"
                      :disabled="isCreateMode"
                      @change="toggleAction(row.moduleKey, 'export')"
                      class="mb-0"
                    >
                      <span class="small" :class="{ 'text-success permission-override-text': row.overridden.export }">{{ $t('message.permission_action_export') }}</span>
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="!!(row.cell.delete && row.cell.delete.allowed)"
                      :class="row.overridden.delete ? 'custom-control-success' : 'custom-control-primary'"
                      :disabled="isCreateMode"
                      @change="toggleAction(row.moduleKey, 'delete')"
                      class="mb-0"
                    >
                      <span class="small" :class="{ 'text-success permission-override-text': row.overridden.delete }">{{ $t('message.permission_action_delete') }}</span>
                    </b-form-checkbox>
                  </div>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-collapse>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BButton, BCollapse, BTableSimple, BTbody, BTr, BTd,
  BFormCheckbox, BSpinner, BModal, VBModal, BPopover, BAlert,
} from 'bootstrap-vue'
import MODULE_ACTION_NOTES from '@/composables/permissionModuleNotes'

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    BCard,
    BCardTitle,
    BButton,
    BCollapse,
    BTableSimple,
    BTbody,
    BTr,
    BTd,
    BFormCheckbox,
    BSpinner,
    BModal,
    BPopover,
    BAlert,
  },
  props: {
    isLoading: { type: Boolean, required: true },
    groups: { type: Array, required: true },
    expanded: { type: Object, required: true },
    allExpanded: { type: Boolean, required: true },
    hasOverrides: { type: Boolean, required: true },
    toggleAll: { type: Function, required: true },
    viewMode: { type: Function, required: true },
    setViewMode: { type: Function, required: true },
    toggleAction: { type: Function, required: true },
    resetOverride: { type: Function, required: true },
    isCreateMode: { type: Boolean, default: false },
  },
  methods: {
    moduleNotes(moduleKey) {
      return MODULE_ACTION_NOTES[moduleKey] || []
    },
  },
}
</script>

<style scoped>
.permission-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 1.5rem;
  row-gap: 4px;
}
.module-name {
  font-weight: 500;
}
.module-note-icon {
  cursor: pointer;
  vertical-align: -2px;
}
.worker-permission-matrix td {
  vertical-align: top;
}
.worker-permission-matrix th:first-child,
.worker-permission-matrix td:first-child {
  padding-left: 1.5rem;
}

/* Marca de "permiso personalizado": el <span> del label es markup propio de este
   componente (no algo que renderiza b-form-checkbox internamente), así que alcanza con
   una clase normal, sin ::v-deep ni pelear con la cascada/transition de Bootstrap. */
.permission-override-text {
  font-weight: 500;
}
</style>
